/*
 *   Copyright (c) 2023 Propally.Inc
 *   All rights reserved.
 */

import { Adjust, AdjustConfig, AdjustEnvironment, AdjustEvent } from "@awesome-cordova-plugins/adjust";
import { Capacitor } from "@capacitor/core";
import { PushNotifications, Token } from "@capacitor/push-notifications";
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics"
import { AppTrackingTransparency } from "capacitor-plugin-app-tracking-transparency";
import dayjs from "dayjs";

import { setAppDeviceToken } from "./storage";

/*
 *   アプリ版かどうか
 */
export const isNativeApp = Capacitor.getPlatform() !== "web";

/*
 *   iOS端末かどうか
 */
export const isIos = Capacitor.getPlatform() === "ios";

/*
 *   Android端末かどうか
 */
export const isAndroid = Capacitor.getPlatform() === "android";

/*
 *   本番環境かどうか
 */
export const isProduction = process.env.NODE_ENV === "production";

/**
 * プッシュ通知初期化処理
 *
 * ref https://capacitorjs.jp/docs/guides/push-notifications-firebase
 */
export const initializePushNotification = (): void => {
  // アプリでない場合は何もしない
  if (!isNativeApp) return;

  // MEMO
  // アプリ起動時とログアウト時(なぜログアウトを検知して発火できるかは不明)に実行される処理
  // そのため初回利用時にプッシュ通知の許可をせず、後から設定で通知を許可した場合にもアプリを一度killして開かれれば有効になるはず
  // Request permission to use push notifications
  // iOS will prompt user and return if they granted permission or not
  // Android will just grant without prompting
  PushNotifications.requestPermissions().then(result => {
    if (result.receive === "granted") {
      console.log("プッシュ通知オン");
      // Register with Apple / Google to receive push via APNS/FCM
      PushNotifications.register().then(r => {
        console.log("プッシュ通知初期化_成功: ", r);
      }).catch(r => {
        console.error("プッシュ通知初期化_失敗: ", r);
      })
    } else {
      console.log("プッシュ通知オフ");
    }
  });

  // MEMO
  // アプリ起動時とログアウト時(なぜログアウトを検知して発火できるかは不明)に実行される処理 ※アプリをkillしていない状態で再度開いた時は開いた際は実行されない
  // On success, we should be able to receive notifications
  PushNotifications.addListener("registration", (token: Token) => {
    console.log("トークン取得: ", token.value);

    // ログイン時にユーザIDとtoken.value(デバイスID)を紐づけるためにローカルストレージにデバイスIDを保存しておく
    setAppDeviceToken(token.value);
  });

  // MEMO
  // プッシュ通知登録失敗時の処理を記述
  // Some issue with our setup and push will not work
  PushNotifications.addListener("registrationError", (error: any) => {
    console.error("プッシュ通知登録失敗: ", JSON.stringify(error));
  });

  // MEMO
  // いったん特に利用はしないためコメントアウト
  // アプリからログアウトした際に溜まっているプッシュ通知を取得してalert()等で表示可能. なぜログアウトを検知して発火できるかは不明
  //
  // PushNotifications.getDeliveredNotifications().then((deliveredNotification) => {
  //   alert("デリバード: " + JSON.stringify(deliveredNotification));
  // });

  // MEMO
  // いったんは特に利用しないためコメントアウト
  // pushNotificationReceivedはアプリを開いている時にプッシュ通知が来た際に実行される処理を記述する
  // notificationはPushNotificationSchemaの型定義を確認すれば分かるが、オブジェクトでtitleやbody, badge等が格納されておりそれらをalert()等で表示可能
  // Show us the notification payload if the app is open on our device
  //
  // PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
  //   console.log("プッシュ受信");
  //   alert("Push received: " + JSON.stringify(notification));
  // });

  // MEMO
  // いったんは特に利用しないためコメントアウト
  // アプリ側で通知センター等に表示されているプッシュ通知を押してアプリを開いた際に実行される処理を記述する
  // notificationはActionPerformedの型定義を確認すれば分かるが、中にフィールドに「notification: PushNotificationSchema;」を持っており、
  // PushNotificationSchemaにはtitleやbody, badge等が格納されているため、それらをalert()等で表示可能
  // Method called when tapping on a notification
  //
  // PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
  //   console.log("プッシュ通知タップ時");
  //   alert("Push action performed: " + JSON.stringify(notification));
  // });
};

/**
 *   計測ツール初期化処理
 *
 *   計測ツールはAdjustを利用
 *   参考: https://github.com/adjust/cordova_sdk
 *   ※AppsFlyer等の別の計測ツールに変更する際はこの中のロジックのみ修正する
 */
export const initializeAppMeasurementTool = (): void => {
  if (!isProduction) {
    console.log("本番環境以外のため実際の計測ツール初期化処理は行わず本ログ出力のみ実施");
    return;
  }

  // アプリでない場合は何もしない
  if (!isNativeApp) return;

  // MEMO
  // トークンは管理画面から取得 https://suite.adjust.com/mobile-apps/fbzvwbfd4cu8/sdk-integration
  // トークンは変数化せずベタで指定しないと認識されなかった
  const adjustConfig = new AdjustConfig("fbzvwbfd4cu8", AdjustEnvironment.Production);
  Adjust.create(adjustConfig);
  console.log("計測ツール初期化処理完了");
};

export const initializeATT = (): void => {
  if (!isProduction) {
    console.log("本番環境以外のためATT初期化処理は行わず本ログ出力のみ実施");
    return;
  }

  // ios以外は処理スキップ
  if (!isIos) return;

  AppTrackingTransparency.requestPermission().then(result => {
    console.log("ATT status:", result.status);
  }).catch(error => {
    console.error(error);
  });
};

/**
 *   計測ツール用イベント名Enum
 */
export enum AppEventTokenEnum {
  // eslint-disable-next-line no-unused-vars
  UserRegistrationCompleted = "q1vwtd",
  // eslint-disable-next-line no-unused-vars
  UserRegistrationFailed = "2zxp61",
  // eslint-disable-next-line no-unused-vars
  UserAccountCompleted = "tc2u55",
  // eslint-disable-next-line no-unused-vars
  CashflowFirstAccess = "mh14v3",
  // eslint-disable-next-line no-unused-vars
  CashflowSecondOnwardsAccess = "uhxng9",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyBasicInfoCompleted = "sfww6n",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyPriceLoanCompleted = "2j9nru",
  // eslint-disable-next-line no-unused-vars
  OwnPropertyCompleted = "pl1t6x",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyBasicInfoCompleted = "mb7tuy",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyPriceLoanCompleted = "368a0m",
  // eslint-disable-next-line no-unused-vars
  ConsiderationPropertyCompleted = "kglxxt",
  // eslint-disable-next-line no-unused-vars
  PropertyNearbyPriceFirstAccess = "sx4sr4",
  // eslint-disable-next-line no-unused-vars
  PropertyNearbyPriceSecondOnwardsAccess = "yg59dq",
  // eslint-disable-next-line no-unused-vars
  MypageFirstAccess = "81sm78",
  // eslint-disable-next-line no-unused-vars
  MypageSecondOnwardsAccess = "dode27",
  // eslint-disable-next-line no-unused-vars
  PropertyManagementFirstAccess = "3ds719",
  // eslint-disable-next-line no-unused-vars
  PropertyManagementSecondOnwardsAccess = "dwrnbd",
  // eslint-disable-next-line no-unused-vars
  OfferFirstAccess = "ea8rp1",
  // eslint-disable-next-line no-unused-vars
  OfferSecondOnwardsAccess = "xvjnsl",
  // eslint-disable-next-line no-unused-vars
  SettingFirstAccess = "o6aed4",
  // eslint-disable-next-line no-unused-vars
  SettingSecondOnwardsAccess = "8u7cl2",
  // eslint-disable-next-line no-unused-vars
  TabOwnPropertyFirstAccess = "gr9s5j",
  // eslint-disable-next-line no-unused-vars
  TabOwnPropertySecondOnwardsAccess = "t139r3",
  // eslint-disable-next-line no-unused-vars
  TabConsiderationPropertyFirstAccess = "9qosj5",
  // eslint-disable-next-line no-unused-vars
  TabConsiderationSecondOnwardsAccess = "5c4xd5",
  // eslint-disable-next-line no-unused-vars
  PropertyTabBalanceOfPaymentsFirstAccess = "3kdxhw",
  // eslint-disable-next-line no-unused-vars
  PropertyTabBalanceOfPaymentsSecondOnwardsAccess = "e63rzz",
  // eslint-disable-next-line no-unused-vars
  PropertyTabSimulationFirstAccess = "476zsu",
  // eslint-disable-next-line no-unused-vars
  PropertyTabSimulationSecondOnwardsAccess = "3gmym5",
  // eslint-disable-next-line no-unused-vars
  PropertyTabInformationFirstAccess = "ya6ko8",
  // eslint-disable-next-line no-unused-vars
  PropertyTabInformationSecondOnwardsAccess = "6ha3nl",
  // eslint-disable-next-line no-unused-vars
  RegistrationTopAccess = "akkom3",
  // eslint-disable-next-line no-unused-vars
  RegistrationRegistrationAccess = "wysayz",
  // eslint-disable-next-line no-unused-vars
  RegistrationSmsCodeInputAccess = "ua8rs3",
  // eslint-disable-next-line no-unused-vars
  RegistrationSmsNumberInputAccess = "xm2j7w",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailTopAccess = "vo2td5",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailFirstAccess = "a941iy",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailSecondAccess = "oskie8",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailThirdAccess = "lbdr20",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailFourthAccess = "64el3k",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailFifthAccess = "3yffxd",  
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailSixthAccess = "6vn788",
  // eslint-disable-next-line no-unused-vars
  RegistrationDetailSeventhAccess = "q6tj84",
  // MEMO 以降はrenewal_v1前に利用していたイベントで現在利用していない
  // eslint-disable-next-line no-unused-vars
  PropertyPriceCompleted = "iqet5f",
  // eslint-disable-next-line no-unused-vars
  PropertyLoanCompleted = "duigr1",
  // eslint-disable-next-line no-unused-vars
  PropertyCashflowCompleted = "ww0nxl",
  // eslint-disable-next-line no-unused-vars
  PropertyMiscCompleted = "iuafrm",
  // eslint-disable-next-line no-unused-vars
  DashboardFirstAccess = "8ouqhi",
  // eslint-disable-next-line no-unused-vars
  DashboardSecondOnwardsAccess = "ree9ux",
  // eslint-disable-next-line no-unused-vars
  ManagementFirstAccess = "6acbbp",
  // eslint-disable-next-line no-unused-vars
  ManagementSecondOnwardsAccess = "s9nthw",
  // eslint-disable-next-line no-unused-vars
  ManagementConsiderationFirstAccess = "xyem51",
  // eslint-disable-next-line no-unused-vars
  ManagementConsiderationSecondOnwardsAccess = "nxzj47",
  // eslint-disable-next-line no-unused-vars
  SimulationFirstAccess = "b8d32f",
  // eslint-disable-next-line no-unused-vars
  SimulationSecondOnwardsAccess = "7jtbf1",
  // eslint-disable-next-line no-unused-vars
  ComparisonFirstAccess = "ddih0t",
  // eslint-disable-next-line no-unused-vars
  ComparisonSecondOnwardsAccess = "a1j3ln",
  // eslint-disable-next-line no-unused-vars
  InvestmentFirstAccess = "e848zx",
  // eslint-disable-next-line no-unused-vars
  InvestmentSecondOnwardsAccess = "rg9qm8",
  // eslint-disable-next-line no-unused-vars
  AssessmentFirstAccess = "le7dxn",
  // eslint-disable-next-line no-unused-vars
  AssessmentSecondOnwardsAccess = "8suvfe",
  // eslint-disable-next-line no-unused-vars,
  ActionedInvestmentFirstAccess = "y2swcz",
  // eslint-disable-next-line no-unused-vars,
  ActionedInvestmentSecondOnwardsAccess = "zi9rag",
  // eslint-disable-next-line no-unused-vars,
  ActionedAssessmentFirstAccess = "aycgka",
  // eslint-disable-next-line no-unused-vars,
  ActionedAssessmentSecondOnwardsAccess = "iyqa34",
  // eslint-disable-next-line no-unused-vars,
  AccountFirstAccess = "87upq8",
  // eslint-disable-next-line no-unused-vars,
  AccountSecondOnwardsAccess = "ghi35a",
  // eslint-disable-next-line no-unused-vars,
  SignoutFirstAccess = "bryjv6",
  // eslint-disable-next-line no-unused-vars,
  SignoutSecondOnwardsAccess = "2m5k4y",
  // eslint-disable-next-line no-unused-vars,
  ReportFirstAccess = "cxi4v9",
  // eslint-disable-next-line no-unused-vars,
  ReportSecondOnwardsAccess = "jehjxh"
}

/**
 * AppEventTokenEnum の値からキー名を取得
 */
const getAppEventTokenEnumKeyByValue = (eventTokenEnumValue: AppEventTokenEnum): string => {
  return Object.keys(AppEventTokenEnum).find(key => AppEventTokenEnum[key] === eventTokenEnumValue);
}

/**
 * サイドバーイベント送信時の 初回起動 or 2回目以降起動の判定ロジック
 * アプリkillを取得するのが難しかったため、代替えの判定として「ユーザ登録日時から24h以内のアクセス」を初回起動とすることとした
 */
export const isFirstAccess = (created_at: string): boolean => {
  const now = dayjs();
  const createdAt = dayjs(created_at);
  return now.diff(createdAt, "hour") <= 24;
}

/**
 *   アプリ計測ツールへのアプリ内イベント送信処理
 */
export const sendEventToAppMeasurementTool = (eventTokenEnum: AppEventTokenEnum): void => {
  // 計測ツールはAdjust & Firebaseを利用 ※AppsFlyer等の別の計測ツールに変更する際はこの中のロジックのみ修正する

  const eventName = getAppEventTokenEnumKeyByValue(eventTokenEnum);
  if (!isProduction) {
    console.log(`本番環境以外のため計測ツールへのイベント送信は行わず本ログ出力のみ実施。eventToken: ${eventTokenEnum}, eventName: ${eventName}`);
    return;
  }

  // アプリでない場合は何もしない
  if (!isNativeApp) return;

  sendAdjustEvent(eventTokenEnum);
  sendFirebaseLogEvent(eventName);
};

/**
 *   Adjust計測へのイベント送信処理
 */
const sendAdjustEvent = (eventToken: string) => {
  Adjust.trackEvent(new AdjustEvent(eventToken));
}

/**
 *   Firebase計測へのイベント送信処理
 */
const sendFirebaseLogEvent = (eventName: string, params?: Object) => {
  FirebaseAnalytics.logEvent({
    name: eventName,
    params: params ? params : {},
  }).then(() => {})
}
